import { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { FileText, Download, Loader2, User, Phone, Search, Receipt, CreditCard } from 'lucide-react';
import API from '../../lib/api';
import { initiatePayUPayment } from '../../lib/payu';

export default function LabsStoreBookingsTab({ bookings, loading, onRefresh, filters, onFiltersChange }) {
  const [payingId, setPayingId] = useState(null);
  const [localSearch, setLocalSearch] = useState(filters?.search || '');

  const payDueFromWallet = async (orderId) => {
    setPayingId(orderId);
    try {
      await API.post(`/labs-store/doctor/bookings/${orderId}/pay-due`);
      toast.success('Due amount cleared from wallet');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Insufficient wallet balance. Try paying via PayU instead.');
    } finally {
      setPayingId(null);
    }
  };

  const payDueViaPayU = async (orderId) => {
    setPayingId(orderId);
    try {
      await initiatePayUPayment({ token: localStorage.getItem('clinic_token'), purpose: 'labs_store_due', orderId });
    } catch (err) {
      toast.error(err.message || 'Failed to start PayU payment');
      setPayingId(null);
    }
  };

  const applyFilters = () => onFiltersChange?.({ ...filters, search: localSearch });

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="labs-store-bookings-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search patient, phone, order number..."
            className="pl-9"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            data-testid="labs-store-bookings-search"
          />
        </div>
        <Input
          type="date"
          className="w-40"
          value={filters?.date_from || ''}
          onChange={(e) => onFiltersChange?.({ ...filters, date_from: e.target.value })}
          data-testid="labs-store-bookings-date-from"
        />
        <Input
          type="date"
          className="w-40"
          value={filters?.date_to || ''}
          onChange={(e) => onFiltersChange?.({ ...filters, date_to: e.target.value })}
          data-testid="labs-store-bookings-date-to"
        />
        <Button size="sm" onClick={applyFilters} data-testid="labs-store-bookings-filter-btn">Filter</Button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="labs-store-bookings-empty">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>{(filters?.search || filters?.date_from || filters?.date_to) ? 'No bookings match your filters.' : 'No lab bookings made on your behalf yet.'}</p>
        </div>
      ) : (
        bookings.map((b) => (
          <Card key={b.id} data-testid={`labs-store-booking-${b.id}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{b.order_number}</p>
                  <p className="text-xs text-slate-500">{new Date(b.created_at).toLocaleString()} • Booked by {b.vendor_name}</p>
                </div>
                <Badge className={b.payment_status === 'due' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                  {b.payment_status === 'due' ? `Due ₹${b.due_amount}` : 'Paid'}
                </Badge>
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {b.patient_name}</span>
                <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {b.patient_phone}</span>
              </div>

              <div className="text-sm">
                {b.items?.map((it, idx) => (
                  <p key={idx} className="text-slate-700">{it.item_name} x{it.quantity}</p>
                ))}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm bg-slate-50 rounded-lg p-3">
                <div><p className="text-slate-400 text-xs">B2B Price Paid</p><p className="font-semibold">₹{b.total}</p></div>
                <div><p className="text-slate-400 text-xs">Patient MRP</p><p className="font-semibold">₹{b.patient_mrp_total}</p></div>
                <div><p className="text-slate-400 text-xs">Discount Given</p><p className="font-semibold">₹{b.discount_given}</p></div>
                <div><p className="text-slate-400 text-xs">Your Earning</p><p className="font-semibold text-teal-700">₹{b.doctor_earning}</p></div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap">
                  {b.reports?.length > 0 ? b.reports.map((r) => (
                    <a key={r.id} href={`${process.env.REACT_APP_BACKEND_URL}${r.file_url}`} target="_blank" rel="noreferrer" data-testid={`labs-store-report-download-${r.id}`}>
                      <Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" /> {r.report_title || 'Report'}</Button>
                    </a>
                  )) : <span className="text-xs text-slate-400">Report pending</span>}
                  {b.invoices?.length > 0 && b.invoices.map((inv) => (
                    <a key={inv.id} href={`${process.env.REACT_APP_BACKEND_URL}${inv.file_url}`} target="_blank" rel="noreferrer" data-testid={`labs-store-invoice-download-${inv.id}`}>
                      <Button size="sm" variant="outline" className="text-teal-700 border-teal-200"><Receipt className="w-3.5 h-3.5 mr-1" /> Invoice</Button>
                    </a>
                  ))}
                </div>
                {b.payment_status === 'due' && (
                  <div className="flex gap-2">
                    <Button size="sm" disabled={payingId === b.id} onClick={() => payDueFromWallet(b.id)} data-testid={`labs-store-pay-due-${b.id}`}>
                      {payingId === b.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}Pay ₹{b.due_amount} from Wallet
                    </Button>
                    <Button size="sm" variant="outline" disabled={payingId === b.id} onClick={() => payDueViaPayU(b.id)} data-testid={`labs-store-pay-due-payu-${b.id}`}>
                      <CreditCard className="w-3.5 h-3.5 mr-1" /> Pay via PayU
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
